import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '@/lib/api';
import type {
  EnquiryAlert,
  EnquiryCounts,
  EnquirySource,
  EnquirySummary,
  EnquirySummaryParams,
  PreferencesDocument,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/**
 * Enquiry counts (for the sidebar badge and the Enquiries page) and the
 * new-enquiry alerts, off `GET /enquiries/summary`, polled while a tab is open.
 *
 * The polling shape is copied from `use-task-reminders.ts` on purpose: a
 * chained, jittered `setTimeout` rather than an interval, exponential backoff
 * on failure, one `AbortController` per load, and a reload when a tab that has
 * been hidden comes back.
 *
 * ## Watermarks are on the ACCOUNT, not in this tab
 *
 * "Which enquiries has this person already been alerted to" is stored in
 * `/preferences` under {@link PREFERENCE_KEY}, one last-seen id per section, so
 * an alert shown on the desktop is not shown again on the laptop (owner's
 * decision: remembered on the account). The document is RE-READ on every poll
 * and merged with this tab's copy by `max`, so a second tab picks up the first
 * tab's advance on its next poll rather than re-popping the same enquiry.
 *
 * ⚠️ **A missing watermark is SEEDED, never treated as zero.** Owner's
 * decision: only enquiries that arrive after launch alert — so the first load
 * for an account asks the summary WITHOUT that section's `…After` key and
 * writes the section's current `latestIds` value as the starting line. Treating
 * a missing watermark as `0` would pop the whole backlog on first sign-in.
 *
 * ⚠️ **During a support session nothing is persisted and nothing is shown.**
 * The operator is reading somebody else's account; advancing the target's
 * watermarks would silently swallow alerts the target never saw.
 */

/** The `/preferences` key. Its shape is {@link Watermarks}. */
const PREFERENCE_KEY = 'enquiryAlerts';

const POLL_MIN_MS = 60_000;
const POLL_MAX_MS = 120_000;
const MAX_BACKOFF_MS = 600_000;
const REFRESH_AFTER_HIDDEN_MS = 60_000;
const MAX_ALERTS = 3;

type WatermarkKey = 'eventInterest' | 'b2bProposal' | 'reportDownload';
type Watermarks = Record<WatermarkKey, number | null>;

const KEYS: readonly WatermarkKey[] = ['eventInterest', 'b2bProposal', 'reportDownload'];

const KEY_OF_SOURCE: Record<EnquirySource, WatermarkKey> = {
  'event-interest': 'eventInterest',
  'b2b-proposal': 'b2bProposal',
  'report-download': 'reportDownload',
};

const PARAM_OF_KEY: Record<WatermarkKey, keyof EnquirySummaryParams> = {
  eventInterest: 'eventInterestAfter',
  b2bProposal: 'b2bProposalAfter',
  reportDownload: 'reportDownloadAfter',
};

const EMPTY_WATERMARKS: Watermarks = { eventInterest: null, b2bProposal: null, reportDownload: null };

function isWatermark(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/** Reads the stored branch defensively — the preferences document is unvalidated. */
function watermarksOf(document: PreferencesDocument): Watermarks {
  const branch = document[PREFERENCE_KEY];
  if (branch === null || typeof branch !== 'object' || Array.isArray(branch)) {
    return { ...EMPTY_WATERMARKS };
  }
  const record = branch as Record<string, unknown>;
  const out: Watermarks = { ...EMPTY_WATERMARKS };
  for (const key of KEYS) {
    const value = record[key];
    if (isWatermark(value)) out[key] = value;
  }
  return out;
}

/** `max` per key, where a known number always beats an unknown one. */
function mergeWatermarks(a: Watermarks, b: Watermarks): Watermarks {
  const out: Watermarks = { ...EMPTY_WATERMARKS };
  for (const key of KEYS) {
    const x = a[key];
    const y = b[key];
    out[key] = x === null ? y : y === null ? x : Math.max(x, y);
  }
  return out;
}

function sameWatermarks(a: Watermarks, b: Watermarks): boolean {
  return KEYS.every((key) => a[key] === b[key]);
}

function alertKey(alert: EnquiryAlert): string {
  return `${alert.source}:${alert.id}`;
}

/**
 * The figure the badges show: the UNASSIGNED count per section (owner,
 * 2026-10-16), falling back per section to the open count when the server is
 * older and does not send `unassignedCounts` (or omits a key). A `null` from
 * the server is kept — it means "cannot read", and is never replaced.
 */
function badgeCountsOf(summary: EnquirySummary): EnquiryCounts {
  const unassigned = summary.unassignedCounts;
  const out: EnquiryCounts = { ...summary.counts };
  if (!unassigned) return out;
  for (const key of KEYS) {
    const value = unassigned[key];
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/** `null` if every count is `null` (or nothing has loaded); never a `?? 0`. */
function totalOf(counts: EnquiryCounts | null): number | null {
  if (!counts) return null;
  let total: number | null = null;
  for (const key of KEYS) {
    const value = counts[key];
    if (value !== null) total = (total ?? 0) + value;
  }
  return total;
}

export interface EnquirySummaryValue {
  /**
   * The UNASSIGNED enquiries per section (open ones on an older server), for
   * the tab pills. `null` until the first load. A `null` FIELD means the
   * reader cannot read that section.
   */
  counts: EnquiryCounts | null;
  /** Sum of the non-null counts — the sidebar badge; `null` when all are null or nothing has loaded. */
  total: number | null;
  /** Reload now — after an archive, convert, delete, assign or unassign on the Enquiries page. */
  refresh: () => void;
  /** Toasts currently shown, newest last, at most three. */
  alerts: EnquiryAlert[];
  dismissAlert: (alert: EnquiryAlert) => void;
}

const NOOP = () => {};

/** Outside the provider: nothing loaded, nothing to refresh, nothing to show. */
const DEFAULT_VALUE: EnquirySummaryValue = {
  counts: null,
  total: null,
  refresh: NOOP,
  alerts: [],
  dismissAlert: NOOP,
};

const EnquirySummaryContext = createContext<EnquirySummaryValue>(DEFAULT_VALUE);

export function EnquirySummaryProvider({ children }: { children: ReactNode }) {
  const { user, supportSession } = useAuth();
  const userId = user?.id ?? null;
  const inSupport = supportSession !== null;

  /*
   * State is tagged with the identity it was loaded for, and anything tagged
   * with another identity reads as empty — so a different person (sign-out,
   * sign-in, support start or end) starts clean without a reset effect.
   */
  const identity = userId === null ? null : `${userId}:${inSupport ? 'support' : 'own'}`;
  const [countsState, setCountsState] = useState<{ identity: string; counts: EnquiryCounts } | null>(
    null,
  );
  const [alertsState, setAlertsState] = useState<{ identity: string; alerts: EnquiryAlert[] }>({
    identity: '',
    alerts: [],
  });
  const [nonce, setNonce] = useState(0);

  const counts = countsState && countsState.identity === identity ? countsState.counts : null;
  const alerts = useMemo(
    () => (alertsState.identity === identity ? alertsState.alerts : []),
    [alertsState, identity],
  );

  /** This tab's copy of the account's watermarks; `null` until read once. */
  const watermarksRef = useRef<Watermarks | null>(null);
  /** Every alert this tab has ever shown, so a re-poll cannot show it twice. */
  const shownRef = useRef(new Set<string>());
  /** Whose watermarks the two refs above hold. */
  const identityRef = useRef<string | null>(null);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  const dismissAlert = useCallback((alert: EnquiryAlert) => {
    const key = alertKey(alert);
    setAlertsState((current) => ({
      identity: current.identity,
      alerts: current.alerts.filter((item) => alertKey(item) !== key),
    }));
  }, []);

  useEffect(() => {
    if (identity === null) return;
    const loadedFor = identity;
    // A different person starts clean: their own watermarks, nothing shown yet.
    // A `refresh()` for the same person keeps both.
    if (identityRef.current !== identity) {
      identityRef.current = identity;
      watermarksRef.current = null;
      shownRef.current = new Set();
    }

    let disposed = false;
    let inflight: AbortController | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let lastLoadAt = 0;
    let failures = 0;

    /**
     * Fire-and-forget: a failed write leaves this tab's copy advanced (so the
     * same enquiry does not re-pop HERE) and the next successful write carries
     * the whole branch again. Scalars replace under the server's deep merge,
     * so sending all three is safe.
     */
    const persist = (marks: Watermarks) => {
      const branch: Record<string, number> = {};
      for (const key of KEYS) {
        const value = marks[key];
        if (value !== null) branch[key] = value;
      }
      api.updatePreferences({ preferences: { [PREFERENCE_KEY]: branch } }).catch(() => {});
    };

    const queueNextPoll = () => {
      clearTimeout(pollTimer);
      const base = POLL_MIN_MS + Math.random() * (POLL_MAX_MS - POLL_MIN_MS);
      const delay =
        failures === 0 ? base : Math.min(base * 2 ** Math.min(failures, 3), MAX_BACKOFF_MS);
      pollTimer = setTimeout(load, delay);
    };

    async function run(signal: AbortSignal) {
      // Support sessions neither read nor write watermarks, and never alert.
      let marks: Watermarks | null = null;
      if (!inSupport) {
        try {
          const prefs = await api.getPreferences(signal);
          const stored = watermarksOf(prefs.data.preferences);
          marks = watermarksRef.current ? mergeWatermarks(watermarksRef.current, stored) : stored;
          watermarksRef.current = marks;
        } catch (caught) {
          if (signal.aborted) throw caught;
          // Unknown watermarks: fetch counts only, seed nothing, alert nothing.
          marks = null;
        }
      }

      const params: EnquirySummaryParams = {};
      if (marks) {
        for (const key of KEYS) {
          const value = marks[key];
          if (value !== null) params[PARAM_OF_KEY[key]] = value;
        }
      }

      const response = await api.enquirySummary(params, signal);
      if (disposed || signal.aborted) return;
      const summary = response.data;
      setCountsState({ identity: loadedFor, counts: badgeCountsOf(summary) });

      if (!marks) return;
      const asked: Watermarks = marks;

      const next: Watermarks = { ...asked };

      // Seed any section that has never had a watermark — the backlog is silent.
      for (const key of KEYS) {
        if (next[key] === null) next[key] = summary.latestIds[key] ?? 0;
      }

      if (summary.isAlertRecipient && summary.alerts.length > 0) {
        const fresh = summary.alerts.filter((alert) => {
          const key = KEY_OF_SOURCE[alert.source];
          // Never an alert for a section this request did not ask about.
          const seenUpTo = key === undefined ? null : asked[key];
          if (seenUpTo === null) return false;
          if (alert.id <= seenUpTo) return false;
          return !shownRef.current.has(alertKey(alert));
        });

        for (const alert of summary.alerts) {
          const key = KEY_OF_SOURCE[alert.source];
          if (key === undefined || asked[key] === null) continue;
          next[key] = Math.max(next[key] ?? 0, alert.id);
        }

        if (fresh.length > 0) {
          for (const alert of fresh) shownRef.current.add(alertKey(alert));
          setAlertsState((state) => {
            const current = state.identity === loadedFor ? state.alerts : [];
            const seen = new Set(current.map(alertKey));
            const merged = [...current, ...fresh.filter((alert) => !seen.has(alertKey(alert)))];
            // Newest last; the oldest falls off past the cap.
            return { identity: loadedFor, alerts: merged.slice(-MAX_ALERTS) };
          });
        }
      }

      // Not on the popup list: keep the line moving with what exists, so being
      // added to the list later starts from that moment instead of replaying
      // whatever arrived in between.
      if (!summary.isAlertRecipient) {
        for (const key of KEYS) {
          const latest = summary.latestIds[key];
          if (latest !== null) next[key] = Math.max(next[key] ?? 0, latest);
        }
      }

      if (!sameWatermarks(next, asked)) {
        watermarksRef.current = next;
        persist(next);
      }
    }

    function load() {
      inflight?.abort();
      const controller = new AbortController();
      inflight = controller;
      lastLoadAt = Date.now();

      run(controller.signal)
        .then(() => {
          if (disposed || controller.signal.aborted) return;
          failures = 0;
        })
        .catch(() => {
          if (disposed || controller.signal.aborted) return;
          // The last good counts stay on screen.
          failures += 1;
        })
        .finally(() => {
          if (disposed || controller.signal.aborted) return;
          queueNextPoll();
        });
    }

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastLoadAt < REFRESH_AFTER_HIDDEN_MS) return;
      load();
    };

    load();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(pollTimer);
      inflight?.abort();
    };
  }, [identity, inSupport, nonce]);

  const value = useMemo<EnquirySummaryValue>(
    () => ({
      counts,
      total: totalOf(counts),
      refresh,
      alerts: inSupport ? [] : alerts,
      dismissAlert,
    }),
    [counts, refresh, alerts, dismissAlert, inSupport],
  );

  return <EnquirySummaryContext.Provider value={value}>{children}</EnquirySummaryContext.Provider>;
}

export function useEnquirySummary(): EnquirySummaryValue {
  return useContext(EnquirySummaryContext);
}
